import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EmptyState, FloatingDoodle, Loader } from "../../components";
import api from "../../services/api";
import logger from "../../utils/logger";
import { IoArrowDown, IoArrowUp, IoCheckmarkOutline, IoClose, IoCopyOutline, IoInformationCircleOutline, IoRefresh, IoTimeOutline, IoWarningOutline } from "react-icons/io5";
import spaceshipDoodle from "../../assets/images/doodles/spaceship-white.png";
import spaceDoodle from "../../assets/images/doodles/space-white.png";
import starDoodle from "../../assets/images/doodles/star-white.png";
import "./index.css";

const POLL_INTERVAL = 60000;

const timeRanges = [
  { key: "last15Minutes", label: "15 min", description: "Last 15 minutes", duration: 15, unit: "Minutes" },
  { key: "lastHour", label: "1 Hour", description: "Last hour", duration: 1, unit: "Hours" },
  { key: "last12Hours", label: "12 Hours", description: "Last 12 hours", duration: 12, unit: "Hours" },
  { key: "last24Hours", label: "24 Hours", description: "Last 24 hours", duration: 24, unit: "Hours" },
];

const DEFAULT_TIME_RANGE = "last24Hours";
const TIME_RANGE_QUERY_PARAM = "timeRange";

function isValidTimeRange(value) {
  return timeRanges.some(range => range.key === value);
}

function getSelectedRangeFromUrl() {
  const queryRange = new URLSearchParams(window.location.search).get(TIME_RANGE_QUERY_PARAM);
  return isValidTimeRange(queryRange) ? queryRange : DEFAULT_TIME_RANGE;
}

function updateSelectedRangeUrl(rangeKey) {
  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set(TIME_RANGE_QUERY_PARAM, rangeKey);
  window.history.pushState({}, "", nextUrl);
}

function formatDateTime(value) {
  if (!value) {
    return "Unavailable";
  }

  return new Intl.DateTimeFormat("en-NZ", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function getLogTotal(logs) {
  return (logs?.infoLogsCount ?? 0) + (logs?.warningLogsCount ?? 0) + (logs?.errorLogsCount ?? 0);
}

function getApplicationStatus(logs) {
  if ((logs?.errorLogsCount ?? 0) > 0) {
    return "error";
  }

  if ((logs?.warningLogsCount ?? 0) > 0) {
    return "warning";
  }

  return "healthy";
}

function getLogLevelTone(logLevel) {
  return logLevel?.toLowerCase() ?? "info";
}

function getAttentionTotal(logs) {
  return (logs?.warningLogsCount ?? 0) + (logs?.errorLogsCount ?? 0);
}

function getLogKey(log) {
  return log.id ?? `${log.timeStamp}-${log.logLevel}-${log.message}`;
}

function getLogCopyText(log) {
  return [formatDateTime(log.timeStamp), log.logLevel ?? "Info", log.message].filter(Boolean).join(" | ");
}

function getLogDetails(response) {
  const records = Array.isArray(response) ? response : response?.data ?? [];

  return records
    .flatMap(record => record?.logs?.logs ?? [])
    .sort((left, right) => new Date(right.timeStamp).getTime() - new Date(left.timeStamp).getTime());
}

function renderStatCard({ tone, icon, value, label }) {
  return (
    <div className={`home-stat-card ${tone}`}>
      <div className="home-stat-icon" aria-hidden="true">{icon}</div>
      <div className="home-stat-content">
        <div className="home-stat-value">{value.toLocaleString()}</div>
        <div className="home-stat-label">{label}</div>
      </div>
    </div>
  );
}

function renderLogCount({ label, value, tone }) {
  return (
    <div className={`log-count ${tone}`}>
      <span className="log-count-value">{value.toLocaleString()}</span>
      <span className="log-count-label">{label}</span>
    </div>
  );
}

function HomePage() {
  const [selectedRange, setSelectedRange] = useState(getSelectedRangeFromUrl);
  const [liveData, setLiveData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedApplication, setExpandedApplication] = useState(null);
  const [detailLogs, setDetailLogs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [activeIssueIndex, setActiveIssueIndex] = useState(0);
  const [copiedLogKey, setCopiedLogKey] = useState(null);
  const detailListRef = useRef(null);
  const copyResetTimeoutRef = useRef(null);

  const selectedRangeMeta = timeRanges.find(range => range.key === selectedRange) ?? timeRanges[0];

  const loadLiveData = useCallback(async ({ showLoader = false } = {}) => {
    if (showLoader) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const data = await api.getLiveData();
      setLiveData(Array.isArray(data) ? data : []);
      setLastUpdated(new Date());
      setError(null);
    } catch (loadError) {
      logger.error("Error loading live logging data:", loadError);
      setError("Unable to load live logging data right now.");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadLiveData({ showLoader: true });
    const pollingInterval = window.setInterval(() => {
      loadLiveData();
    }, POLL_INTERVAL);

    return () => {
      window.clearInterval(pollingInterval);
    };
  }, [loadLiveData]);

  useEffect(() => {
    function handleHistoryChange() {
      setSelectedRange(getSelectedRangeFromUrl());
    }

    window.addEventListener("popstate", handleHistoryChange);

    return () => {
      window.removeEventListener("popstate", handleHistoryChange);
    };
  }, []);

  useEffect(() => {
    if (!expandedApplication) {
      setDetailLogs([]);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }

    let isCurrentRequest = true;

    async function loadApplicationLogs() {
      setDetailLoading(true);
      setDetailError(null);

      try {
        const response = await api.getLogs(
          expandedApplication,
          selectedRangeMeta.duration,
          selectedRangeMeta.unit
        );

        if (isCurrentRequest) {
          setDetailLogs(getLogDetails(response));
        }
      } catch (loadError) {
        logger.error("Error loading application logs:", loadError);

        if (isCurrentRequest) {
          setDetailLogs([]);
          setDetailError("Unable to load logs for this application right now.");
        }
      } finally {
        if (isCurrentRequest) {
          setDetailLoading(false);
        }
      }
    }

    loadApplicationLogs();

    return () => {
      isCurrentRequest = false;
    };
  }, [expandedApplication, selectedRangeMeta.duration, selectedRangeMeta.unit]);

  useEffect(() => {
    setActiveIssueIndex(0);
  }, [detailLogs, expandedApplication, selectedRange]);

  useEffect(() => {
    return () => {
      window.clearTimeout(copyResetTimeoutRef.current);
    };
  }, []);

  const dashboardRows = useMemo(() => {
    return liveData
      .map(application => ({
        ...application,
        logs: application?.[selectedRange] ?? {},
      }))
      .sort((left, right) => {
        const leftLogs = left.logs;
        const rightLogs = right.logs;
        return (
          (rightLogs.errorLogsCount ?? 0) - (leftLogs.errorLogsCount ?? 0)
          || (rightLogs.warningLogsCount ?? 0) - (leftLogs.warningLogsCount ?? 0)
          || getLogTotal(rightLogs) - getLogTotal(leftLogs)
          || left.applicationName.localeCompare(right.applicationName)
        );
      });
  }, [liveData, selectedRange]);

  const totals = useMemo(() => {
    return dashboardRows.reduce((summary, application) => {
      summary.info += application.logs?.infoLogsCount ?? 0;
      summary.warnings += application.logs?.warningLogsCount ?? 0;
      summary.errors += application.logs?.errorLogsCount ?? 0;
      return summary;
    }, { info: 0, warnings: 0, errors: 0 });
  }, [dashboardRows]);

  const totalLogs = totals.info + totals.warnings + totals.errors;

  const applicationsNeedingAttention = useMemo(() => {
    return dashboardRows.filter(application => getAttentionTotal(application.logs) > 0);
  }, [dashboardRows]);

  const applicationsWithNoLogs = useMemo(() => {
    return dashboardRows.filter(application => getLogTotal(application.logs) === 0);
  }, [dashboardRows]);

  const topAttentionApplication = applicationsNeedingAttention[0];

  const attentionLogs = useMemo(() => {
    return detailLogs
      .map((log, detailIndex) => ({ ...log, detailIndex }))
      .filter(log => getLogLevelTone(log.logLevel) === "error");
  }, [detailLogs]);

  const scrollToIssue = useCallback((nextIndex) => {
    if (attentionLogs.length === 0) {
      return;
    }

    const boundedIndex = (nextIndex + attentionLogs.length) % attentionLogs.length;
    setActiveIssueIndex(boundedIndex);

    const detailListElement = detailListRef.current;
    const issueElement = detailListElement?.querySelector(`[data-issue-index="${boundedIndex}"]`);

    if (!issueElement || !detailListElement) {
      return;
    }

    const detailListRect = detailListElement.getBoundingClientRect();
    const issueRect = issueElement.getBoundingClientRect();
    const targetTop = detailListElement.scrollTop
      + issueRect.top
      - detailListRect.top
      - (detailListElement.clientHeight / 2)
      + (issueElement.clientHeight / 2);

    detailListElement.scrollTo({
      top: Math.max(0, targetTop),
      behavior: "smooth",
    });
  }, [attentionLogs.length]);

  const copyLog = useCallback(async (log) => {
    const logKey = getLogKey(log);

    try {
      await navigator.clipboard.writeText(getLogCopyText(log));
      setCopiedLogKey(logKey);
      window.clearTimeout(copyResetTimeoutRef.current);
      copyResetTimeoutRef.current = window.setTimeout(() => setCopiedLogKey(null), 2000);
    } catch (copyError) {
      logger.error("Error copying log entry:", copyError);
    }
  }, []);

  const selectRange = useCallback((rangeKey) => {
    setSelectedRange(rangeKey);
    updateSelectedRangeUrl(rangeKey);
  }, []);

  return (
    <div className="home-page">

      <section className={`home-topbar home-command-band ${totals.errors > 0 ? "error" : totals.warnings > 0 ? "warning" : "healthy"}`} aria-label="Logging dashboard summary">
        <div className="home-topbar-left">
          <h2 className="home-brand-title">Logging Overview</h2>
          <div className="home-range-toggle" role="group" aria-label="Log time range">
            {timeRanges.map(range => (
              <button
                key={range.key}
                type="button"
                className={`home-range-button ${selectedRange === range.key ? "active" : ""}`}
                aria-pressed={selectedRange === range.key}
                onClick={() => selectRange(range.key)}
              >
                <span>{range.label}</span>
              </button>
            ))}
          </div>
          <p className="home-updated-text" aria-live="polite">
            <IoRefresh className={isRefreshing ? "spinning" : ""} aria-hidden="true" />
            {lastUpdated ? `Updated ${formatDateTime(lastUpdated)}` : "Updating..."}
          </p>
        </div>

        <div className="home-topbar-right">
          <div className="home-stats-grid" aria-label="Log totals">
            {renderStatCard({ tone: "primary", icon: <IoTimeOutline />, value: totalLogs, label: "Total logs" })}
            {renderStatCard({ tone: "success", icon: <IoInformationCircleOutline />, value: totals.info, label: "Info logs" })}
            {renderStatCard({ tone: "warning", icon: <IoWarningOutline />, value: totals.warnings, label: "Warnings" })}
            {renderStatCard({ tone: "error", icon: <IoWarningOutline />, value: totals.errors, label: "Errors" })}
          </div>

          <div className="home-topbar-bottom">
            <div className={`home-operations-strip ${totals.errors > 0 ? "error" : totals.warnings > 0 ? "warning" : "healthy"}`} aria-label="Logging attention summary">
              <div>
                <span className="home-operations-label">Current focus</span>
                <strong>{topAttentionApplication?.applicationName ?? "All applications clear"}</strong>
              </div>
              <div>
                <span className="home-operations-label">Apps needing attention</span>
                <strong>{applicationsNeedingAttention.length.toLocaleString()}</strong>
              </div>
            </div>
          </div>
        </div>

        {applicationsWithNoLogs.length > 0 && (
          <div className="home-no-logs-tile" role="status" aria-label="Applications with no logs reporting">
            <span className="home-operations-label">No logs reporting</span>
            <strong>{applicationsWithNoLogs.length.toLocaleString()} apps may not be running</strong>
            <ul>
              {applicationsWithNoLogs.map(application => (
                <li key={application.applicationName}>{application.applicationName}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <div className="home-content">
        <section className="home-app-list-panel" aria-label="Application log counts">
          <div className="home-panel-header">
            <div>
              <h3>Application Activity</h3>
              <p>{selectedRangeMeta.label} window, sorted by items needing attention.</p>
            </div>
          </div>

          {isLoading && <Loader content="Loading live logs" />}

          {!isLoading && error && (
            <EmptyState
              title="Live logs unavailable"
              message={error}
              className="home-empty-state"
            />
          )}

          {!isLoading && !error && dashboardRows.length === 0 && (
            <EmptyState
              title="No live log data"
              message="No applications returned data for this dashboard yet."
              className="home-empty-state"
            />
          )}

          {!isLoading && !error && dashboardRows.length > 0 && (
            <div className="home-log-list">
              {dashboardRows.map(application => {
                const logs = application.logs;
                const status = getApplicationStatus(logs);
                const isSelected = expandedApplication === application.applicationName;

                return (
                  <article className={`home-log-row ${status} ${isSelected ? "selected" : ""}`} key={application.applicationName}>
                    <button
                      type="button"
                      className="home-log-summary"
                      aria-pressed={isSelected}
                      onClick={() => setExpandedApplication(currentApplication => (
                        currentApplication === application.applicationName ? null : application.applicationName
                      ))}
                    >
                      <div className="home-log-app">
                        <span className={`home-log-status ${status}`} aria-label={`${status} status`} />
                        <div>
                          <h4>{application.applicationName}</h4>
                          <p>Last {application?.lastErrorRecordedAt ?  `Error At: ${formatDateTime(application.lastErrorRecordedAt)}` : `Log: ${formatDateTime(application?.lastLogRecordedAt)}`}</p>
                        </div>
                      </div>
                      <div className="home-log-counts" aria-label={`${application.applicationName} log counts`}>
                        {renderLogCount({ label: "Info", value: logs.infoLogsCount ?? 0, tone: "info" })}
                        {renderLogCount({ label: "Warnings", value: logs.warningLogsCount ?? 0, tone: "warning" })}
                        {renderLogCount({ label: "Errors", value: logs.errorLogsCount ?? 0, tone: "error" })}
                      </div>
                    </button>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {expandedApplication && (
        <>
          <div
            className="home-drawer-overlay"
            onClick={() => setExpandedApplication(null)}
            aria-hidden="true"
          />
          <aside className="home-drawer" aria-label="Application log details">
            <div className="home-drawer-header">
              <div>
                <span className="home-operations-label">Expanded stream</span>
                <h3>{expandedApplication}</h3>
              </div>
              <div className="home-drawer-actions">
                {!detailLoading && !detailError && detailLogs.length > 0 && (
                  <div className="home-issue-controls" aria-label="Issue navigation">
                    <strong>{detailLogs.length.toLocaleString()} logs, {attentionLogs.length.toLocaleString()} issues</strong>
                    {attentionLogs.length > 0 && (
                      <>
                        <span>{activeIssueIndex + 1} of {attentionLogs.length}</span>
                        <button type="button" onClick={() => scrollToIssue(activeIssueIndex - 1)} aria-label="Jump to previous issue">
                          <IoArrowUp aria-hidden="true" />
                          Previous
                        </button>
                        <button type="button" onClick={() => scrollToIssue(activeIssueIndex + 1)} aria-label="Jump to next issue">
                          <IoArrowDown aria-hidden="true" />
                          Next
                        </button>
                      </>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="home-drawer-close"
                  onClick={() => setExpandedApplication(null)}
                  aria-label="Close log details"
                >
                  <IoClose aria-hidden="true" />
                  Close
                </button>
              </div>
            </div>

            <div className="home-drawer-body">
              {detailLoading && <Loader content={`Loading ${selectedRangeMeta.label} logs`} />}

              {!detailLoading && detailError && (
                <EmptyState
                  title="Logs unavailable"
                  message={detailError}
                  className="home-empty-state"
                />
              )}

              {!detailLoading && !detailError && detailLogs.length === 0 && (
                <EmptyState
                  title="No logs found"
                  message="No log entries were returned for this application and timeframe."
                  className="home-empty-state"
                />
              )}

              {!detailLoading && !detailError && detailLogs.length > 0 && (
                <div className="home-log-detail-list" aria-label={`${expandedApplication} log entries`} ref={detailListRef}>
                  {detailLogs.map((log, detailIndex) => {
                    const logKey = getLogKey(log);
                    const tone = getLogLevelTone(log.logLevel);
                    const issueIndex = attentionLogs.findIndex(issueLog => issueLog.detailIndex === detailIndex);
                    const isIssue = issueIndex >= 0;
                    const isActiveIssue = isIssue && issueIndex === activeIssueIndex;
                    const isCopied = copiedLogKey === logKey;

                    return (
                      <div
                        className={`home-log-detail-item ${tone} ${isActiveIssue ? "active-issue" : ""}`}
                        key={logKey}
                        data-issue-index={isIssue ? issueIndex : undefined}
                      >
                        <time dateTime={log.timeStamp}>{formatDateTime(log.timeStamp)}</time>
                        <span className="home-log-level">{log.logLevel ?? "Info"}</span>
                        <p>{log.message}</p>
                        <button
                          type="button"
                          className={`home-log-copy-button ${isCopied ? "copied" : ""}`}
                          aria-label={`Copy ${log.logLevel ?? "Info"} log from ${formatDateTime(log.timeStamp)}`}
                          onClick={() => copyLog(log)}
                        >
                          {isCopied ? <IoCheckmarkOutline aria-hidden="true" /> : <IoCopyOutline aria-hidden="true" />}
                          <span>{isCopied ? "Copied" : "Copy"}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
        </>
      )}
            <FloatingDoodle source={spaceshipDoodle} className="home-doodle home-doodle-spaceship" duration={5000} distance={14} />
      <FloatingDoodle source={spaceDoodle} className="home-doodle home-doodle-space" duration={6500} distance={10} />
      <FloatingDoodle source={starDoodle} className="home-doodle home-doodle-star" duration={4000} distance={8} />
    </div>
  );
}

export default memo(HomePage);
